//  Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {Component, OnInit} from '@angular/core';
import {SecurityRequestService} from '../../../../services/security-request.service';
import {MatDialog} from '@angular/material/dialog';
import {RestConfigService} from '../../../../services/config-service';
import {catchError, switchMap} from 'rxjs/operators';
import {EMPTY, forkJoin} from 'rxjs';
import AuthHelper from '../../../auth/authHelper';
import {
    ISecurityRequestResponse,
    SecurityApprovalMethod,
    SecurityRequestStatus,
    SecurityRequestType
} from '../../../../types/security-requests';
import {NotificationService} from '../../../../services/notification-service';
import {TranslateService} from '@ngx-translate/core';
import {ISecurityRequestDialogResult, SecurityRequestDialogComponent} from '../security-request-dialog/security-request-dialog.component';
import {SecurityRequestStatusDialogComponent} from '../security-request-status-dialog/security-request-status-dialog.component';
import {AdminSettingsService} from '../../../../services/admin-settings.service';

@Component({
    selector: 'app-recovery-key-request',
    templateUrl: './recovery-key-request.component.html',
    styleUrls: ['./recovery-key-request.component.scss'],
    standalone: false
})
export class RecoveryKeyRequestComponent implements OnInit {
    securityRequestsEnabled = false;
    minimumApprovalPercentRequired: number = 0;
    recoveryKeyAccessDurationMinutes: number = 0;
    currentRequest: ISecurityRequestResponse;
    buttonDisabled = true;
    securityApprovalMethod = SecurityApprovalMethod.PERCENT_RULE;

    readonly Status = SecurityRequestStatus;

    constructor(private readonly securityRequestService: SecurityRequestService,
                private readonly dialog: MatDialog,
                private readonly translationService: TranslateService,
                private readonly adminSettingsService: AdminSettingsService,
                private readonly notificationService: NotificationService,
                private readonly restConfigService: RestConfigService,) {
    }

    ngOnInit(): void {
        forkJoin({
            restConfig: this.restConfigService.getRestConfig(),
            adminSettings: this.adminSettingsService.getAdminSettings(),
            currentRequest: this.securityRequestService.getCurrentRequest(),
        }).subscribe(({restConfig, adminSettings, currentRequest}) => {
            this.securityRequestsEnabled = restConfig.security_requests_enabled && adminSettings.enable_recovery_key;
            this.securityApprovalMethod = adminSettings.security_approval_method;
            this.minimumApprovalPercentRequired = restConfig.security_request_min_percent;
            this.recoveryKeyAccessDurationMinutes = restConfig.recovery_key_access_duration_minutes;
            this.currentRequest = currentRequest;
            this.setButtonDisabled();
        });
    }

    setButtonDisabled() {
        if (this.currentRequest) {
            if (this.currentRequest.status == SecurityRequestStatus.ACCEPTED) {
                const acceptedAt = new Date(this.currentRequest.accepted);
                const durationMs = this.recoveryKeyAccessDurationMinutes * 60 * 1000;
                const now = new Date();
                const diffMs = Math.round(now.valueOf() - acceptedAt.valueOf());
                this.buttonDisabled = diffMs < durationMs;
            } else {
                this.buttonDisabled = this.currentRequest.status == SecurityRequestStatus.PENDING ||
                    this.currentRequest.status == SecurityRequestStatus.APPROVED;
            }
        } else {
            this.buttonDisabled = true;
        }
    }

    requestRecoveryKeyAccess() {
        this.securityRequestService.getApprovers().pipe(
            switchMap(response => {
                const dialogRef = this.dialog.open(
                    SecurityRequestDialogComponent,
                    {
                        width: '44em',
                        data: {
                            results: response.results,
                            minimumApprovalPercentRequired: this.minimumApprovalPercentRequired,
                            requestTitle: this.translationService.instant('RECOVERY_KEY_ACCESS_DIALOG_TITLE'),
                            securityApprovalMethod: this.securityApprovalMethod
                        },
                    }
                );
                return dialogRef.afterClosed();
            }),
            switchMap((dialogResult: ISecurityRequestDialogResult) => {
                if (dialogResult) {
                    const userId = AuthHelper.getUidFromToken();
                    return this.securityRequestService.postSecurityRequest(
                      userId, SecurityRequestType.RECOVERY_KEY, null,  null, dialogResult.notifyApprovers);
                }
                return EMPTY;
            }),
            switchMap(() => {
                return this.securityRequestService.getCurrentRequest();
            }),
            catchError(error => {
                console.error(error);
                this.notificationService.showError(this.translationService.instant('ERROR_OCCURRED_UNKNOWN'));
                return EMPTY;
            })
        ).subscribe(currentRequest => {
            this.currentRequest = currentRequest;
            this.setButtonDisabled();
            this.securityRequestService.refresh$.next(null);
            this.notificationService.showSuccess(this.translationService.instant('SUCCESS'));
        })
    }

    viewRecoveryKeyStatus(requestId: number) {
        this.securityRequestService.getCurrentRequest().pipe(
            switchMap(currentRequest => {
                this.currentRequest = currentRequest;
                return this.securityRequestService.getApproversStatus(requestId);
            }),
            switchMap(response => {
                const dialogRef = this.dialog.open(
                    SecurityRequestStatusDialogComponent,
                    {
                        width: '44em',
                        data: {
                            results: response.results,
                            minimumApprovalPercentRequired: this.minimumApprovalPercentRequired,
                            requestStatus: this.currentRequest.status,
                            dialogTitle: this.translationService.instant('RECOVERY_KEY_ACCESS_STATUS_DIALOG_TITLE'),
                            securityApprovalMethod: this.securityApprovalMethod
                        }
                    }
                );
                return dialogRef.afterClosed();
            }),
            switchMap((buttonClicked: boolean) => {
                if (buttonClicked) {
                    if (this.currentRequest.status == SecurityRequestStatus.APPROVED) {
                        return this.securityRequestService.putAcceptRequest(this.currentRequest.id);
                    } else if (this.currentRequest.status == SecurityRequestStatus.PENDING) {
                        return this.securityRequestService.putCancelRequest(this.currentRequest.id);
                    }
                }
                return EMPTY;
            }),
            switchMap(() => {
                return this.securityRequestService.getCurrentRequest();
            }),
            catchError(() => {
                this.notificationService.showError(this.translationService.instant('ERROR_OCCURRED_UNKNOWN'));
                return EMPTY;
            })
        ).subscribe(currentRequest => {
            this.currentRequest = currentRequest;
            this.setButtonDisabled();
            this.securityRequestService.refresh$.next(null);
            this.notificationService.showSuccess(this.translationService.instant('SUCCESS'));
        });
    }
}
